import { useState } from 'react'
import { Truck } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useExpressCompanies } from '@/hooks/useExpressCompany'
import type { Order, DeliveryParams, SplitDeliveryParams } from '@/schemas/supplier'

interface DeliveryDialogProps {
  order: Order
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (data: DeliveryParams | SplitDeliveryParams) => void
  isLoading?: boolean
}

export function DeliveryDialog({
  order,
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: DeliveryDialogProps) {
  const [deliveryType, setDeliveryType] = useState<'1' | '2' | '3'>('1')
  const [expressCode, setExpressCode] = useState('')
  const [trackingNumber, setTrackingNumber] = useState('')
  const [virtualContent, setVirtualContent] = useState('')
  const [remark, setRemark] = useState('')
  const [splitMode, setSplitMode] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Map<string, number>>(new Map())
  const {
    data: expressCompanies = [],
    isLoading: isExpressLoading,
    isError: isExpressError,
  } = useExpressCompanies({ enabled: open && deliveryType === '1' })

  const selectedCompany = expressCompanies.find((company) => company.code === expressCode)

  // 切换商品选择
  const handleItemToggle = (cartId: string, maxNum: number) => {
    const newSelected = new Map(selectedItems)
    if (newSelected.has(cartId)) {
      newSelected.delete(cartId)
    } else {
      newSelected.set(cartId, maxNum)
    }
    setSelectedItems(newSelected)
  }

  // 更新发货数量
  const handleQuantityChange = (cartId: string, quantity: number, maxNum: number) => {
    const newSelected = new Map(selectedItems)
    if (quantity > 0 && quantity <= maxNum) {
      newSelected.set(cartId, quantity)
    }
    setSelectedItems(newSelected)
  }

  // 验证表单
  const isValid = () => {
    const hasSelectedSplitItems = !splitMode || selectedItems.size > 0

    if (deliveryType === '1') {
      return Boolean(selectedCompany && trackingNumber.trim() && !isExpressError && hasSelectedSplitItems)
    } else if (deliveryType === '2') {
      return hasSelectedSplitItems
    } else {
      return Boolean(virtualContent.trim() && hasSelectedSplitItems)
    }
  }

  // 确认发货
  const handleConfirm = () => {
    if (!isValid()) return

    const baseParams: DeliveryParams = {
      type: deliveryType,
      remark: remark || undefined,
    }

    // 根据发货类型填充参数
    if (deliveryType === '1') {
      if (!selectedCompany) return
      baseParams.delivery_code = selectedCompany.code
      baseParams.delivery_name = selectedCompany.name
      baseParams.delivery_id = trackingNumber.trim()
    } else if (deliveryType === '3') {
      baseParams.fictitious_content = virtualContent
    }

    // 拆单模式
    if (splitMode) {
      if (selectedItems.size === 0) return

      const splitParams: SplitDeliveryParams = {
        ...baseParams,
        cart_ids: Array.from(selectedItems.entries()).map(([cart_id, cart_num]) => ({
          cart_id,
          cart_num,
        })),
      }
      onConfirm(splitParams)
    } else {
      onConfirm(baseParams)
    }
  }

  // 重置状态
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setDeliveryType('1')
      setExpressCode('')
      setTrackingNumber('')
      setVirtualContent('')
      setRemark('')
      setSplitMode(false)
      setSelectedItems(new Map())
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            订单发货
          </DialogTitle>
          <DialogDescription>
            订单号：{order.order_id} | 收货人：{order.real_name} {order.user_phone}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* 发货类型选择 */}
          <div className="space-y-3">
            <Label>发货类型</Label>
            <div className="flex gap-4">
              {[
                { value: '1', label: '快递发货' },
                { value: '2', label: '送货上门' },
                { value: '3', label: '虚拟发货' },
              ].map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="deliveryType"
                    value={option.value}
                    checked={deliveryType === option.value}
                    onChange={() => setDeliveryType(option.value as '1' | '2' | '3')}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 快递信息 */}
          {deliveryType === '1' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>快递公司 *</Label>
                <Select
                  value={expressCode}
                  onValueChange={setExpressCode}
                  disabled={isExpressLoading || isExpressError}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isExpressLoading ? '物流公司加载中' : '请选择快递公司'} />
                  </SelectTrigger>
                  <SelectContent>
                    {expressCompanies.map((company) => (
                      <SelectItem key={company.code} value={company.code}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isExpressError && (
                  <p className="text-sm text-destructive">物流公司加载失败</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>快递单号 *</Label>
                <Input
                  placeholder="请输入快递单号"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* 虚拟发货内容 */}
          {deliveryType === '3' && (
            <div className="space-y-2">
              <Label>虚拟发货内容 *</Label>
              <Textarea
                placeholder="请输入虚拟发货内容，如卡密、下载链接等"
                value={virtualContent}
                onChange={(e) => setVirtualContent(e.target.value)}
                rows={3}
              />
            </div>
          )}

          {/* 商品列表（拆单模式） */}
          {order.cart_info && order.cart_info.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>商品信息</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSplitMode(!splitMode)
                    if (!splitMode) {
                      // 进入拆单模式，默认全选
                      const allSelected = new Map<string, number>()
                      order.cart_info?.forEach((item) => {
                        allSelected.set(item.cart_id, item.cart_num)
                      })
                      setSelectedItems(allSelected)
                    } else {
                      setSelectedItems(new Map())
                    }
                  }}
                >
                  {splitMode ? '取消拆单' : '拆单发货'}
                </Button>
              </div>
              <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                {order.cart_info.map((item) => (
                  <div key={item.cart_id} className="flex items-center gap-3 p-3">
                    {splitMode && (
                      <Checkbox
                        checked={selectedItems.has(item.cart_id)}
                        onCheckedChange={() => handleItemToggle(item.cart_id, item.cart_num)}
                      />
                    )}
                    <img
                      src={item.product_image}
                      alt={item.product_name}
                      className="w-12 h-12 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.product_name}</p>
                      {item.product_attr && (
                        <p className="text-xs text-muted-foreground">{item.product_attr}</p>
                      )}
                    </div>
                    <div className="text-right flex items-center gap-2">
                      {splitMode && selectedItems.has(item.cart_id) ? (
                        <Input
                          type="number"
                          min={1}
                          max={item.cart_num}
                          value={selectedItems.get(item.cart_id) || item.cart_num}
                          onChange={(e) =>
                            handleQuantityChange(item.cart_id, Number(e.target.value), item.cart_num)
                          }
                          className="w-16 h-8 text-center"
                        />
                      ) : (
                        <span className="text-sm">x{item.cart_num}</span>
                      )}
                      <span className="text-sm text-muted-foreground w-16">
                        ¥{item.price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 备注 */}
          {deliveryType !== '3' && (
            <div className="space-y-2">
              <Label>备注</Label>
              <Textarea
                placeholder="选填，可输入发货备注"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={2}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleConfirm} disabled={!isValid() || isLoading}>
            {isLoading ? '处理中...' : '确认发货'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
